import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Drawer, Spin, Table, Tag} from 'antd'
import {RightOutlined} from '@ant-design/icons'
import {
    getWorkloadStageItems,
    getWorkloadStageSkus,
} from '../../../../services/api/apiProductionPlanning'
import {DASH, formatDate, formatDecimal, formatHours, formatInt, formatRate} from '../../helpers/format'
import Notice from '../shared/Notice'
import LoadError from '../shared/LoadError'

/** Ghi chú theo loại công đoạn (E4) — chỉ hiện đúng một ghi chú phù hợp. */
const STAGE_NOTES = {
    machine: 'Công đoạn do máy quyết định nhịp: số người quy đổi dẫn xuất từ ràng buộc máy (số máy chưa làm tròn × số người trên một máy), không từ giờ người. Nhờ vậy mức tải phản ánh đúng nút thắt là máy.',
    by_detect: 'Công đoạn tính giờ theo dữ liệu file in: giờ công cộng theo từng dòng vật liệu của item. Sản lượng vẫn đếm theo item — hai việc tách rời.',
    by_order: 'Công đoạn đếm theo đơn: một đơn nhiều item chỉ phát sinh một lượt qua công đoạn.',
    photo: 'Công đoạn Chụp ảnh sản phẩm chỉ tính trên phần hàng được yêu cầu chụp, không tính trên toàn bộ lượng hàng.',
}

/**
 * Khung trượt xem chi tiết (E) của SCR-M17-001. Hai mức: mức SKU (E-A) và mức item (E-B).
 *
 * Mọi chi tiết mở bằng khung trượt bên phải, không điều hướng sang trang khác (§1).
 */
function StageDetailDrawer({stageCode, stageName, params, visible, onClose}) {
    const [level, setLevel] = useState('sku')
    const [skuId, setSkuId] = useState(null)
    const [skuData, setSkuData] = useState(null)
    const [itemData, setItemData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const fetchSkus = useCallback(async () => {
        if (!stageCode) return

        setLoading(true)
        setError(null)

        try {
            setSkuData(await getWorkloadStageSkus(stageCode, params))
        } catch (e) {
            // Không có catch thì drawer mở ra chỉ còn dấu `—`: người dùng không phân biệt được
            // "không có dữ liệu" với "request lỗi", và rejection rơi thẳng vào Sentry.
            setError(e)
        } finally {
            setLoading(false)
        }
    }, [stageCode, params])

    useEffect(() => {
        if (!visible) return

        setLevel('sku')
        setSkuId(null)
        setError(null)
        fetchSkus()
    }, [visible, fetchSkus])

    const openItems = async (targetSkuId) => {
        setSkuId(targetSkuId)
        setLevel('item')
        setLoading(true)
        setError(null)

        try {
            setItemData(await getWorkloadStageItems(stageCode, targetSkuId, params))
        } catch (e) {
            setError(e)
        } finally {
            setLoading(false)
        }
    }

    const skuColumns = [
        {
            title: 'SKU',
            dataIndex: 'skuId',
            render: (value, row) => (
                <div>
                    <code className="VcSkuCode">{value}</code>
                    {row.hasItemDetail && <RightOutlined className="VcChevron"/>}
                    {row.sampleItemId && (
                        <div className="VcHint">ví dụ item {row.sampleItemId}</div>
                    )}
                </div>
            ),
        },
        {title: 'Item', dataIndex: 'itemCount', align: 'right', render: formatInt},
        {
            title: 'Giây/item bình quân',
            dataIndex: 'avgSecondsPerItem',
            align: 'right',
            render: (value) => formatDecimal(value, 2),
        },
        {
            title: 'Thấp nhất – cao nhất',
            key: 'range',
            align: 'right',
            render: (text, row) => (row.minSeconds === row.maxSeconds
                // Công đoạn tính theo cả item cho mọi item cùng một số giây → "cố định".
                ? <span className="VcMuted">cố định</span>
                : `${formatDecimal(row.minSeconds, 2)} – ${formatDecimal(row.maxSeconds, 2)}`),
        },
        {title: 'Giờ công', dataIndex: 'hours', align: 'right', render: (value) => formatHours(value, 1)},
        {
            title: '%',
            dataIndex: 'sharePercent',
            align: 'right',
            render: (value) => `${formatDecimal(value, 1)}%`,
        },
    ]

    /*
     * Ba cột riêng của nguồn Sản lượng hiện tại — FR-M17-21 bước 1, BR-N3-16.
     *
     * Bắt buộc phải có ở nguồn này: thuật toán phân bổ chọn item nào là phần người dùng nghi ngờ
     * nhất, và không đối chiếu được tới từng `barcode_item` thì họ sẽ không tin con số. Danh sách đã
     * được backend sắp theo đúng thứ tự thuật toán đã duyệt (created_at từ cũ tới mới — AC-21.2),
     * nên KHÔNG sắp lại ở đây.
     */
    const currentOutputColumns =
        params && params.source === 'currentOutput'
            ? [
                  {title: 'barcode', dataIndex: 'barcode', width: 110},
                  {title: 'barcode_item', dataIndex: 'barcodeItem', width: 150},
                  {
                      title: 'created_at',
                      dataIndex: 'createdAt',
                      width: 120,
                      render: (value) => (value ? formatDate(value) : DASH),
                  },
              ]
            : []

    const itemColumns = [
        {
            title: '#',
            key: 'index',
            width: 44,
            render: (text, row, index) => index + 1,
        },
        ...currentOutputColumns,
        {
            title: 'Dòng vật liệu',
            key: 'lines',
            render: (text, row) => (
                <div>
                    {(row.lines || []).map((line) => (
                        <div key={line.materialCode}>
                            {line.materialName}: {formatInt(line.quantity)} {line.unit} ×{' '}
                            {/* Cùng cách hiện với M16: chỉ số luôn làm tròn LÊN 1 chữ số, không
                                phải 2 chữ số làm tròn gần nhất — hai màn lệch nhau thì người dùng
                                tưởng là hai con số khác nhau. */}
                            {formatRate(line.rate)}
                        </div>
                    ))}
                    {/*
                      * E5b: vật liệu không có nhóm cấu hình nghĩa là KHÔNG đi qua công đoạn đó (gỗ
                      * chỉ cắt, không in). Dòng đó không sinh giờ và không bị coi là thiếu định mức.
                      */}
                    {!!(row.skippedMaterials || []).length && (
                        <div className="VcHint">
                            Không qua công đoạn này:{' '}
                            {row.skippedMaterials.map((item) => item.materialName).join(', ')}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Số giây',
            dataIndex: 'seconds',
            align: 'right',
            width: 100,
            render: (value) => formatDecimal(value, 2),
        },
    ]

    return (
        <Drawer
            width={680}
            visible={visible}
            onClose={onClose}
            title={level === 'sku'
                ? `${stageName} — cách tính & SKU cấu thành`
                : `${stageName} · ${skuId} — danh sách item`}
        >
            <Spin spinning={loading}>
                {!error && level === 'sku' && skuData && (
                    <>
                        <div className="VcFormulaBox">
                            {Object.keys(skuData.formula || {}).map((key) => (
                                <div key={key} className="VcFormulaLine">{skuData.formula[key]}</div>
                            ))}
                        </div>

                        {(skuData.notes || []).map((note) => (
                            STAGE_NOTES[note]
                                ? <Notice key={note} level="info">{STAGE_NOTES[note]}</Notice>
                                : null
                        ))}

                        {!!(skuData.missing
                            && (skuData.missing.noStandardItemCount || skuData.missing.noFileDataItemCount)) && (
                            <Notice level="warning">
                                Không tính được: {formatInt(skuData.missing.noStandardItemCount)} item
                                thiếu định mức · {formatInt(skuData.missing.noFileDataItemCount)} item chưa
                                có dữ liệu file in. Không cộng vào tổng, không thay bằng giá trị đoán.
                            </Notice>
                        )}

                        <Table
                            className="VcTable"
                            rowKey="skuId"
                            size="small"
                            pagination={false}
                            columns={skuColumns}
                            dataSource={skuData.skus}
                            onRow={(row) => ({
                                onClick: () => (row.hasItemDetail ? openItems(row.skuId) : null),
                                className: row.hasItemDetail ? 'VcRow--clickable' : '',
                            })}
                        />

                        {(skuData.skus || []).some((row) => row.hasItemDetail) && (
                            <div className="VcHint mt-2">
                                Bấm một dòng SKU để xem danh sách item và số liệu của từng item.
                            </div>
                        )}
                    </>
                )}

                {!error && level === 'item' && itemData && (
                    <>
                        <Button className="mb-3" onClick={() => setLevel('sku')}>
                            ← Quay lại danh sách SKU
                        </Button>

                        <div className="VcHint mb-2">
                            Hiển thị tối đa {formatInt((itemData.items || []).length)} item đầu tiên trong
                            tổng số {formatInt(itemData.totalItemCount)} item. Mỗi item có số liệu riêng
                            vì diện tích in và đường cắt được gắn theo từng item.
                        </div>

                        <Table
                            className="VcTable"
                            rowKey="itemId"
                            size="small"
                            pagination={false}
                            columns={itemColumns}
                            dataSource={itemData.items}
                        />
                    </>
                )}

                {!loading && error && (
                    <LoadError
                        error={error}
                        message="Không tải được chi tiết công đoạn. Vui lòng thử lại"
                        onRetry={level === 'item' ? () => openItems(skuId) : fetchSkus}
                    />
                )}

                {!loading && !error && level === 'sku' && !skuData && (
                    <Tag>{DASH}</Tag>
                )}
            </Spin>
        </Drawer>
    )
}

StageDetailDrawer.propTypes = {
    stageCode: PropTypes.string,
    stageName: PropTypes.string,
    params: PropTypes.object.isRequired,
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
}

export default StageDetailDrawer
